// ============================================================
//  src/pages/auth/LoginPage.js
// ============================================================
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { getError } from '../../utils/helpers';
import AuthShell, { AuthAlert } from './AuthShell';

export default function LoginPage() {
  const { login }     = useAuth();
  const nav           = useNavigate();
  const loc           = useLocation();
  const [form, setForm]       = useState({ identifier: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.identifier.trim() || !form.password) {
      setError('Enter your email or phone and your password.');
      return;
    }
    setLoading(true); setError('');
    try {
      await login(form.identifier.trim(), form.password);
      toast.success('Welcome back.');
      const next = loc.state?.from || '/dashboard';
      nav(next, { replace: true });
    } catch (err) {
      // Unverified email → redirect to verify page with the email prefilled
      const status = err?.response?.status;
      const extra  = err?.response?.data || {};
      if (status === 403 && extra?.email && err?.response?.data?.code !== 'banned') {
        nav('/verify-email', { state: { email: extra.email } });
        return;
      }
      setError(getError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      headline={<>Welcome back to your <em>always-on</em> WhatsApp team.</>}
      sub="Your bots kept answering customers while you were away. Sign in to see what happened."
      points={['Visual flow builder & JSON actions', 'Agent inbox for human hand-off', 'Broadcasts, schedules & analytics']}
      switchText="New to CommandKit?"
      switchTo="/register"
      switchLabel="Create an account"
      title="Sign in"
      subtitle="Good to see you again. Enter your details to continue."
    >
      <AuthAlert>{error}</AuthAlert>

      <form onSubmit={submit} noValidate>
        <div className="auth-field">
          <label htmlFor="login-id">Email or phone</label>
          <div className="auth-field-wrap">
            <i className="fas fa-at fi" />
            <input
              id="login-id"
              type="text"
              value={form.identifier}
              onChange={set('identifier')}
              placeholder="you@example.com"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
              required
            />
          </div>
        </div>

        <div className="auth-field">
          <div className="auth-label-row">
            <label htmlFor="login-pw">Password</label>
            <Link to="/forgot-password" className="auth-link-sm">Forgot password?</Link>
          </div>
          <div className="auth-field-wrap">
            <i className="fas fa-lock fi" />
            <input
              id="login-pw"
              type={showPwd ? 'text' : 'password'}
              value={form.password}
              onChange={set('password')}
              placeholder="Your password"
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              className="pw-toggle"
              onClick={() => setShowPwd((v) => !v)}
              aria-label={showPwd ? 'Hide password' : 'Show password'}
            >
              <i className={`fas ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
            </button>
          </div>
        </div>

        <button type="submit" className="btn-auth" disabled={loading}>
          {loading
            ? <><span className="spinner" /> Signing in…</>
            : <>Sign in <i className="fas fa-arrow-right" /></>}
        </button>
      </form>

      <div className="auth-bottom">
        Don’t have an account? <Link to="/register">Sign up free</Link>
      </div>
    </AuthShell>
  );
}
